import { useState, useEffect, useRef } from 'react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { X, Info, RefreshCcw, Download, ImageOff } from 'lucide-react';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { toast } from 'sonner';
import SecureImage from './SecureImage';

interface ImageViewDialogProps {
  imageUrl: string;
  title?: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const ImageViewDialog = ({ imageUrl, title, open, onOpenChange }: ImageViewDialogProps) => {
  const [hasError, setHasError] = useState(false);
  const [displayUrl, setDisplayUrl] = useState(imageUrl);
  const retryCountRef = useRef<number>(0);
  const maxRetries = 1; // Only try one alternative format
  
  // Reset state when image URL changes or dialog opens
  useEffect(() => {
    if (open) {
      setDisplayUrl(imageUrl);
      setHasError(false);
      retryCountRef.current = 0;
    }
  }, [imageUrl, open]);
  
  const handleDebugClick = () => {
    toast.info('Image URL logged to console');
    // Copy to clipboard
    navigator.clipboard.writeText(imageUrl)
      .then(() => toast.success('URL copied to clipboard'))
      .catch(err => {
        
        toast.error('Failed to copy URL to clipboard');
      });
  };
  
  // Try alternative URL formats
  const tryAlternativeUrl = () => {
    // Check if we've exceeded retry limit
    if (retryCountRef.current >= maxRetries) {
      toast.error('Maximum retry attempts reached');
      return;
    }
    
    // Increment retry counter
    retryCountRef.current += 1;
    setHasError(false);
    
    // Try path style URL if we're using virtual host style URL
    if (displayUrl.includes('.s3.')) {
      // Extract key from virtual host style URL
      const parts = displayUrl.split('.com/');
      if (parts.length > 1) {
        const key = parts[1];
        const regionStart = displayUrl.indexOf('.s3.') + 4;
        const regionEnd = displayUrl.indexOf('.amazonaws');
        const region = displayUrl.substring(regionStart, regionEnd);
        const bucket = displayUrl.substring(8, displayUrl.indexOf('.s3.'));
        
        // Construct path style URL
        const pathStyleUrl = `https://s3.${region}.amazonaws.com/${bucket}/${key}`;
        setDisplayUrl(pathStyleUrl);
        toast.info('Trying path style URL format');
        
        return;
      }
    } 
    // If we're already using path style URL, try virtual host style
    else if (displayUrl.includes('amazonaws.com/')) {
      const parts = displayUrl.split('amazonaws.com/');
      if (parts.length > 1) {
        const pathParts = parts[1].split('/');
        if (pathParts.length > 1) {
          const bucket = pathParts[0];
          const key = pathParts.slice(1).join('/');
          
          // Extract region
          const regionStart = displayUrl.indexOf('s3.') + 3;
          const regionEnd = displayUrl.indexOf('.amazonaws');
          const region = displayUrl.substring(regionStart, regionEnd);
          
          // Construct virtual host style URL
          const virtualHostUrl = `https://${bucket}.s3.${region}.amazonaws.com/${key}`;
          setDisplayUrl(virtualHostUrl);
          toast.info('Trying virtual host style URL format');
          
          return;
        }
      }
    }
    
    // If all else fails, try the original URL again
    setDisplayUrl(imageUrl);
    toast.info('Retrying with original URL');
  };
  
  const handleDownload = () => {
    // Create a temporary anchor element
    const a = document.createElement('a');
    a.href = displayUrl;
    a.download = title || 'image';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    toast.success('Download started');
  };

  // Handle image load error - only log once
  const handleImageError = () => {
    if (!hasError) {
      
      setHasError(true);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl p-0 overflow-hidden">
        {title ? (
          <DialogTitle className="sr-only">{title}</DialogTitle>
        ) : (
          <VisuallyHidden asChild>
            <DialogTitle>Image Preview</DialogTitle>
          </VisuallyHidden>
        )}
        <DialogDescription className="sr-only">
          A larger view of the selected image
        </DialogDescription>
        <div className="relative">
          {hasError ? (
            <div className="flex flex-col items-center justify-center p-10 bg-gray-100 min-h-[300px]">
              <ImageOff className="w-12 h-12 mb-4 text-gray-400" />
              <p className="mb-4 text-lg text-center text-gray-700">
                Failed to load image
              </p>
              <div className="flex gap-2">
                {retryCountRef.current < maxRetries && (
                  <Button onClick={tryAlternativeUrl} variant="outline" className="flex items-center">
                    <RefreshCcw className="w-4 h-4 mr-2" />
                    Retry with different URL format
                  </Button>
                )}
                <Button onClick={handleDownload} variant="outline" className="flex items-center">
                  <Download className="w-4 h-4 mr-2" />
                  Download directly
                </Button>
              </div>
              <p className="mt-4 text-sm text-center text-gray-500">
                URL: {displayUrl.length > 50 ? `${displayUrl.substring(0, 50)}...` : displayUrl}
              </p>
            </div>
          ) : (
            <SecureImage 
              src={displayUrl} 
              alt={title || 'Image preview'} 
              className="object-contain w-full"
              onError={handleImageError}
              onSignedUrlGenerated={(url) => setDisplayUrl(url)}
            />
          )}
          <div className="absolute top-2 right-2 flex gap-2">
            <Button
              size="icon"
              variant="ghost"
              className="text-white bg-black/20 hover:bg-black/30"
              onClick={handleDebugClick}
              title="Debug image URL"
            >
              <Info className="w-4 h-4" />
            </Button>
            <Button
              size="icon"
              variant="ghost"
              className="text-white bg-black/20 hover:bg-black/30"
              onClick={() => onOpenChange(false)}
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>
        {title && (
          <div className="p-4">
            <h3 className="text-lg font-medium">{title}</h3>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ImageViewDialog; 